import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { updateUsuario } from "../firebase/firestore/usuarios";
import type { Referente, Usuario } from "../firebase/firestore/usuarios";
import { useUsuariosStore } from "../zustand/useUsuariosStore";
import type Fee from "../interfaces/Fee";
import UserRole from "../interfaces/UserRole";

export default function useUserFormik(entity: Usuario) {
  const { fetchAllUsuarios } = useUsuariosStore()
  const [loading, setLoading] = useState(false);

  // Validaciones
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("El nombre es obligatorio"),
    lastName: Yup.string().required("El apellido es obligatorio"),
    email: Yup.string()
      .email("Correo inválido")
      .required("El correo es obligatorio"),
    countryCode: Yup.string()
      .matches(/^\+\d{1,4}$/, "Prefijo inválido")
      .required("El prefijo es obligatorio"),
    phone: Yup.string()
      .matches(/^\d{7,12}$/, "Número de teléfono inválido")
      .required("El teléfono es obligatorio"),
    lockerCode: Yup.string().required("El cliente es obligatorio"),
    role: Yup.mixed<UserRole>()
      .oneOf(Object.values(UserRole), "El rol debe ser Admin, Digitador o Cliente")
      .nullable(),
    estado: Yup.mixed<"Pendiente" | "Confirmado">()
      .oneOf(["Pendiente", "Confirmado"], "Estado inválido")
      .nullable(),
    puntos: Yup.number()
      .typeError("Debe ser un número")
      .min(0, "Debe ser mayor o igual a 0")
      .nullable()
      .transform((value) => (Number.isNaN(value) ? undefined : value)),
    tarifaAerea: Yup.object().nullable().shape({
      nombre: Yup.string().nullable(),
      monto: Yup.number()
        .typeError("Debe ser un número")
        .nullable()
        .transform((value) => (Number.isNaN(value) ? undefined : value)),
      moneda: Yup.string().nullable(),
    }),
    tarifaMaritima: Yup.object().nullable().shape({
      nombre: Yup.string().nullable(),
      monto: Yup.number()
        .typeError("Debe ser un número")
        .nullable()
        .transform((value) => (Number.isNaN(value) ? undefined : value)),
      moneda: Yup.string().nullable(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      id: entity?.id ?? "",
      name: entity?.name ?? "",
      lastName: entity?.lastName ?? "",
      email: entity?.email ?? "",
      countryCode: entity?.countryCode ?? "",
      phone: entity?.phone ?? "",
      lockerCode: entity?.lockerCode ?? "",
      role: entity?.role ?? undefined,
      estado: entity?.estado ?? undefined,
      puntos: entity?.puntos ?? undefined,
      tarifaAerea: entity?.tarifaAerea ?? undefined,
      tarifaMaritima: entity?.tarifaMaritima ?? undefined,
      vendedor: entity?.vendedor ?? undefined,
      referidoPor: entity?.referidoPor ?? undefined,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        // Preparar valores para guardar en Firestore
        const cleanValues: Partial<Usuario> = {
          name: values.name,
          lastName: values.lastName,
          email: values.email,
          countryCode: values.countryCode,
          phone: values.phone,
          lockerCode: values.lockerCode,
          puntos: values.puntos !== undefined && values.puntos !== null ? values.puntos : undefined,
          role: values.role || undefined,
          estado: values.estado || undefined,
          vendedor: (values.vendedor as Referente) || undefined,
          referidoPor: (values.referidoPor as Referente) || undefined,
        };

        // Limpiar tarifa aérea: solo incluir si tiene todos los campos
        if (values.tarifaAerea?.nombre && 
            values.tarifaAerea?.monto !== undefined && 
            values.tarifaAerea?.moneda) {
          cleanValues.tarifaAerea = values.tarifaAerea as Fee;
        } else {
          cleanValues.tarifaAerea = undefined;
        }

        // Limpiar tarifa marítima: solo incluir si tiene todos los campos
        if (values.tarifaMaritima?.nombre && 
            values.tarifaMaritima?.monto !== undefined && 
            values.tarifaMaritima?.moneda) {
          cleanValues.tarifaMaritima = values.tarifaMaritima as Fee;
        } else {
          cleanValues.tarifaMaritima = undefined;
        }
        
        await updateUsuario(values.id, cleanValues);
      } catch (err) {
        console.log({ err });
      } finally {
        setLoading(false);
        fetchAllUsuarios();
      }

    },
  });

  return { ...formik, loading };
}